import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertCircle, Loader2, ArrowLeft } from 'lucide-react';
import { api } from '@/api/api';
import { toast } from 'sonner';
import { authStorage } from '@/utils/authStorage';
import { applyAuthToken } from '@/api/axiosSetup';
import InkPhDLogo from '@/components/InkPhDLogo';

export default function ThesisLogin() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await api.loginAuthor(formData);
      if (response.success && response.user) {
        authStorage.setAuthorAuth(response.user, response.access_token);
        applyAuthToken(response.access_token);
        toast.success('Welcome back');
        navigate('/thesis');
      } else {
        setError('Login failed');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white dark:bg-[#0a0e1a] text-slate-900 dark:text-white"
         style={{ fontFamily: '"Inter", system-ui, sans-serif' }}>
      {/* Top bar — back to landing */}
      <div className="px-6 lg:px-10 py-5 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 text-sm text-slate-500 hover:text-[#1d4ed8] dark:hover:text-[#7aa9ff] transition-colors" data-testid="back-to-landing">
          <ArrowLeft className="w-4 h-4" />
          <span>Back</span>
        </Link>
        <Link to="/" data-testid="login-logo">
          <InkPhDLogo size="md" />
        </Link>
        <div className="w-12" />
      </div>

      {/* Centered login card */}
      <div className="flex-1 flex items-center justify-center px-4 pb-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-10">
            <h1 className="font-serif text-3xl text-[#0b1a35] dark:text-white tracking-tight mb-2"
                style={{ fontFamily: '"Fraunces", Georgia, serif' }}
                data-testid="login-headline">
              Sign in to InkPhD
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Access your scientific writing workspace
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="thesis-login-form">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs uppercase tracking-widest text-slate-500 dark:text-slate-400 font-semibold">Email</Label>
              <Input id="email" type="email" required
                placeholder="you@example.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="h-11 rounded-lg border-slate-300 dark:border-white/10 dark:bg-white/[0.03] focus-visible:ring-[#1d4ed8]"
                data-testid="thesis-login-email"
              />
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-xs uppercase tracking-widest text-slate-500 dark:text-slate-400 font-semibold">Password</Label>
                <Link to="/thesis/forgot-password" className="text-xs text-[#1d4ed8] dark:text-[#7aa9ff] hover:underline" data-testid="forgot-password-link">Forgot?</Link>
              </div>
              <Input id="password" type="password" required
                placeholder="••••••••"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="h-11 rounded-lg border-slate-300 dark:border-white/10 dark:bg-white/[0.03] focus-visible:ring-[#1d4ed8]"
                data-testid="thesis-login-password"
              />
            </div>

            {error && (
              <div className="flex items-start gap-2 text-sm text-rose-600 dark:text-rose-400 bg-rose-50 dark:bg-rose-950/30 border border-rose-100 dark:border-rose-900/40 p-3 rounded-lg" data-testid="thesis-login-error">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <Button type="submit"
              className="w-full h-11 bg-[#0b1a35] hover:bg-[#1d4ed8] text-white rounded-full text-sm font-medium transition-colors shadow-sm hover:shadow-md"
              disabled={loading}
              data-testid="thesis-login-submit">
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Sign in
            </Button>

            <p className="text-xs text-center text-slate-500 dark:text-slate-400 pt-2">
              Don't have an account?{' '}
              <Link to="/author/register" className="text-[#1d4ed8] dark:text-[#7aa9ff] hover:underline font-medium" data-testid="register-link">
                Create one
              </Link>
            </p>
          </form>

          <p className="mt-10 text-[11px] text-center text-slate-400 leading-relaxed">
            By signing in you agree to our{' '}
            <Link to="/legal/terms" className="underline hover:text-[#1d4ed8] dark:hover:text-[#7aa9ff]">Terms of Use</Link>
            {' '}and{' '}
            <Link to="/legal/privacy" className="underline hover:text-[#1d4ed8] dark:hover:text-[#7aa9ff]">Privacy Policy</Link>.
          </p>
        </div>
      </div>

      <div className="px-6 lg:px-10 py-6 text-center text-[11px] text-slate-400 border-t border-slate-100 dark:border-white/5">
        © {new Date().getFullYear()} InkPhD · Research use only — not a medical device.
      </div>
    </div>
  );
}
